import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { SseService } from 'src/app/services/SseService';
import { CookieService } from 'ngx-cookie-service';
import { TemaService } from 'src/app/services/tema.services';
import { ConfirmDialogComponent } from '../../common/confirm-dialog/confirm-dialog.component';

export interface MetaApiAdminCuenta {
  id: number;
  userId: number;
  userEmail: string;
  userAlias: string;
  metaApiAccountId: string;
  mtLogin: string;
  mtServer: string;
  nombre: string;
  region: string;
  apiUrl: string;
  lastActive: string;
  estado: string;
  tokenExpiresAt?: string;
  /** CLOUD = provisionada en MetaAPI; GATEWAY = Nectum MT5 Gateway local (sin deploy) */
  origen?: string;
  // Campos enriquecidos desde MetaAPI provisioning API (opcionales)
  connectionStatus?: string;
  reliability?: string;
  resourceSlots?: number;
  copyFactoryResourceSlots?: number;
  version?: number;
  createdAt?: string;
  connections?: any[];
  type?: string;
}

export interface MetaApiAdminResumen {
  total: number;
  deployed: number;
  undeployed: number;
  deployFailed: number;
  deploying: number;
  syncTime: string;
}

export interface MetaApiApiStats {
  total: number;
  connected: number;
  disconnected: number;
  disconnectedBroker: number;
  highReliability: number;
  totalResourceSlots: number;
  totalCfSlots: number;
  syncTime: string;
}

@Component({
  selector: 'app-admin-metaapi-cuentas',
  templateUrl: './admin-metaapi-cuentas.component.html',
  styleUrls: ['./admin-metaapi-cuentas.component.scss']
})
export class AdminMetaapiCuentasComponent implements OnInit, OnDestroy {

  isLoading = false;
  isEnriqueciendo = false;
  isSyncing:    { [accountId: string]: boolean } = {};
  isDeploying:  { [accountId: string]: boolean } = {};

  displayedColumns: string[] = [
    'userId', 'userEmail', 'mtLogin', 'mtServer', 'nombre', 'origen',
    'region', 'estado', 'tokenExpiresAt', 'lastActive', 'metaApiAccountId', 'acciones'
  ];
  displayedColumnsEnriquecido: string[] = [
    'userId', 'userEmail', 'mtLogin', 'mtServer', 'nombre', 'origen',
    'region', 'estado', 'connectionStatus', 'reliability', 'resourceSlots',
    'version', 'createdAt', 'connections', 'tokenExpiresAt', 'lastActive', 'metaApiAccountId', 'acciones'
  ];

  enriquecido = false;

  dataSource = new MatTableDataSource<MetaApiAdminCuenta>([]);
  resumen: MetaApiAdminResumen = {
    total: 0, deployed: 0, undeployed: 0, deployFailed: 0, deploying: 0, syncTime: ''
  };
  apiStats: MetaApiApiStats | null = null;

  private theme = 'light-theme';
  private temaSub?: Subscription;

  constructor(
    private sseService: SseService,
    private toastr: ToastrService,
    private cdRef: ChangeDetectorRef,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private temaService: TemaService
  ) {}

  ngOnInit(): void {
    this.temaSub = this.temaService.valDarkTheme.subscribe(dark => {
      this.theme = dark ? 'dark-theme' : 'light-theme';
    });
    this.cargar();
  }

  ngOnDestroy(): void {
    this.temaSub?.unsubscribe();
  }

  cargar(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.sseService.apiGetNew('admin/metaapiCuentas').subscribe({
      next: (data: any) => {
        if (data?.status) {
          this.dataSource.data = Array.isArray(data.cuentas) ? data.cuentas : [];
          this.resumen = data.resumen ?? this.resumen;
        } else {
          this.toastr.error('Error al cargar cuentas MetaAPI', 'Error', { timeOut: 5000 });
        }
        this.cdRef.markForCheck();
      },
      error: () => {
        this.toastr.error('No fue posible cargar las cuentas MetaAPI', 'Error', { timeOut: 5000 });
        this.isLoading = false;
        this.cdRef.markForCheck();
      },
      complete: () => {
        this.isLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  syncCuenta(accountId: string): void {
    if (this.isSyncing[accountId]) return;
    this.isSyncing[accountId] = true;
    const licencia = this.cookieService.get('auth_token');
    this.sseService.apiGetWithAuth(`metaapi/estado/${accountId}`, licencia).subscribe({
      next: (data: any) => {
        if (data?.status) {
          const idx = this.dataSource.data.findIndex(c => c.metaApiAccountId === accountId);
          if (idx !== -1) {
            this.dataSource.data[idx].estado = data.state;
            this.dataSource.data = [...this.dataSource.data];
          }
          this.toastr.success(`Estado: ${data.state}`, accountId.substring(0, 8) + '…');
        }
      },
      error: () => { this.toastr.error('Error al sincronizar', 'Sync'); },
      complete: () => { this.isSyncing[accountId] = false; this.cdRef.markForCheck(); }
    });
  }

  enriquecer(): void {
    if (this.isEnriqueciendo) return;
    this.isEnriqueciendo = true;
    this.sseService.apiGetNew('admin/metaapiEstadisticasApi').subscribe({
      next: (data: any) => {
        if (data?.status && Array.isArray(data.cuentas)) {
          // Indexar por _id para fusión O(1)
          const apiMap = new Map<string, any>();
          data.cuentas.forEach((c: any) => apiMap.set(c._id, c));

          this.dataSource.data = this.dataSource.data.map(row => {
            const api = apiMap.get(row.metaApiAccountId);
            if (!api) return row;
            return {
              ...row,
              connectionStatus:          api.connectionStatus  ?? undefined,
              reliability:               api.reliability       ?? undefined,
              resourceSlots:             api.resourceSlots     ?? undefined,
              copyFactoryResourceSlots:  api.copyFactoryResourceSlots ?? undefined,
              version:                   api.version           ?? undefined,
              createdAt:                 api.createdAt         ?? undefined,
              connections:               api.connections       ?? undefined,
              type:                      api.type              ?? undefined,
            };
          });

          this.apiStats   = data.stats ?? null;
          this.enriquecido = true;
          this.toastr.success('Datos enriquecidos desde MetaAPI', '', { timeOut: 2500 });
        } else {
          this.toastr.error('Error al obtener estadísticas de MetaAPI', 'Error');
        }
        this.cdRef.markForCheck();
      },
      error: () => {
        this.toastr.error('No se pudo conectar con la API de MetaAPI', 'Error');
        this.isEnriqueciendo = false;
        this.cdRef.markForCheck();
      },
      complete: () => {
        this.isEnriqueciendo = false;
        this.cdRef.markForCheck();
      }
    });
  }

  get columnas(): string[] {
    return this.enriquecido ? this.displayedColumnsEnriquecido : this.displayedColumns;
  }

  connectionStatusClass(status: string | undefined): string {
    if (!status) return '';
    if (status === 'CONNECTED')              return 'conn-connected';
    if (status === 'DISCONNECTED_FROM_BROKER') return 'conn-broker';
    return 'conn-disconnected';
  }

  isTokenExpired(d: string | undefined): boolean {
    return !!d && new Date(d) < new Date();
  }

  isTokenExpiringSoon(d: string | undefined): boolean {
    if (!d) return false;
    const diff = new Date(d).getTime() - Date.now();
    return diff > 0 && diff < 7 * 24 * 3600 * 1000;
  }

  deployar(accountId: string): void {
    if (this.isDeploying[accountId]) return;
    this.isDeploying[accountId] = true;
    this._actualizarEstadoLocal(accountId, 'DEPLOYING');
    const licencia = this.cookieService.get('auth_token');
    this.sseService.apiPostNew(`metaapi/deploy/${accountId}`, {}, licencia).subscribe({
      next: (data: any) => {
        if (data?.status) {
          this.toastr.success('Desplegando cuenta…', accountId.substring(0, 8) + '…');
        } else {
          this._actualizarEstadoLocal(accountId, 'DEPLOY_FAILED');
          this.toastr.error('No se pudo desplegar', 'Deploy');
        }
      },
      error: () => {
        this._actualizarEstadoLocal(accountId, 'DEPLOY_FAILED');
        this.toastr.error('Error al desplegar la cuenta', 'Deploy');
      },
      complete: () => { this.isDeploying[accountId] = false; this.cdRef.markForCheck(); }
    });
  }

  undeploy(accountId: string): void {
    if (this.isDeploying[accountId]) return;
    this.isDeploying[accountId] = true;
    this._actualizarEstadoLocal(accountId, 'UNDEPLOYING');
    const licencia = this.cookieService.get('auth_token');
    this.sseService.apiPostNew(`metaapi/undeploy/${accountId}`, {}, licencia).subscribe({
      next: (data: any) => {
        if (data?.status) {
          this._actualizarEstadoLocal(accountId, 'UNDEPLOYED');
          this.toastr.success('Cuenta retirada', accountId.substring(0, 8) + '…');
        } else {
          this.toastr.error('Respuesta inesperada del servidor', 'Undeploy');
        }
      },
      error: () => { this.toastr.error('Error al retirar la cuenta', 'Undeploy'); },
      complete: () => { this.isDeploying[accountId] = false; this.cdRef.markForCheck(); }
    });
  }

  eliminar(r: MetaApiAdminCuenta): void {
    const accountId = r.metaApiAccountId;
    if (this.isDeploying[accountId]) return;
    this.dialog.open(ConfirmDialogComponent, {
      width: '420px',
      panelClass: this.theme,
      data: {
        titulo: 'Eliminar cuenta MetaAPI',
        icon: 'delete_forever',
        msj: `Cuenta ${r.mtLogin} (${r.mtServer}) de ${r.userEmail || 'user ' + r.userId}.\n\n` +
             'Se borra el servidor y el historial descargado en MetaAPI. Esta acción es irreversible.',
        botonAceptar: 'Sí, eliminar',
        botonCancelar: 'Cancelar',
        destructivo: true,
      }
    }).afterClosed().subscribe(ok => {
      if (ok) this._eliminarConfirmado(accountId);
    });
  }

  private _eliminarConfirmado(accountId: string): void {
    this.isDeploying[accountId] = true;
    const licencia = this.cookieService.get('auth_token');
    this.sseService.apiPostNew(`metaapi/eliminar/${accountId}`, {}, licencia).subscribe({
      next: (data: any) => {
        if (data?.status) {
          this._actualizarEstadoLocal(accountId, 'REMOVED');
          this.toastr.success('Cuenta eliminada de MetaAPI', accountId.substring(0, 8) + '…');
        } else {
          this.toastr.error(data?.error || 'No se pudo eliminar la cuenta', 'Eliminar');
        }
      },
      error: (err: any) => {
        // El 403 (usuario no administrador) trae el motivo en err.error.error
        this.toastr.error(err?.error?.error || 'Error al eliminar la cuenta', 'Eliminar');
        this.isDeploying[accountId] = false;
        this.cdRef.markForCheck();
      },
      complete: () => { this.isDeploying[accountId] = false; this.cdRef.markForCheck(); }
    });
  }

  private _actualizarEstadoLocal(accountId: string, estado: string): void {
    const idx = this.dataSource.data.findIndex(c => c.metaApiAccountId === accountId);
    if (idx !== -1) {
      this.dataSource.data[idx].estado = estado;
      this.dataSource.data = [...this.dataSource.data];
      this._recalcularResumen();
    }
    this.cdRef.markForCheck();
  }

  private _recalcularResumen(): void {
    const data = this.dataSource.data;
    this.resumen.total        = data.length;
    this.resumen.deployed     = data.filter(c => c.estado === 'DEPLOYED').length;
    this.resumen.undeployed   = data.filter(c => c.estado === 'UNDEPLOYED').length;
    this.resumen.deployFailed = data.filter(c => c.estado === 'DEPLOY_FAILED').length;
    this.resumen.deploying    = data.filter(c => c.estado === 'DEPLOYING' || c.estado === 'UNDEPLOYING').length;
  }

  canDeploy(estado: string): boolean {
    return estado === 'UNDEPLOYED' || estado === 'DEPLOY_FAILED' || estado === 'CREATED';
  }

  canUndeploy(estado: string): boolean {
    return estado === 'DEPLOYED';
  }

  canDelete(estado: string): boolean {
    return estado !== 'REMOVED';
  }

  isTransitioning(estado: string): boolean {
    return estado === 'DEPLOYING' || estado === 'UNDEPLOYING';
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() =>
      this.toastr.success('Copiado', '', { timeOut: 1500 })
    );
  }

  estadoClass(estado: string): string {
    const map: { [k: string]: string } = {
      'DEPLOYED':     'estado-deployed',
      'DEPLOYING':    'estado-deploying',
      'DEPLOY_FAILED': 'estado-failed',
      'REMOVED':      'estado-removed'
    };
    return map[estado] || 'estado-undeployed';
  }
}
